import { parse } from 'query-string';
import { FieldNames } from 'app/models/fields/names';
import { storage } from 'app/util/storage';
import { getProduct, PRODUCT } from 'app/util/headers';
import i18next from 'i18next';
import { logger } from 'app/util/logger';

interface ParsedQueryParams {
  loanOfficerId?: string;
  lng?          : string;
  loanPurpose?  : string;
}

const TRACKING_KEYS = [ 'utm_campaign', 'utm_source', 'utm_medium', 'utm_content', 'utm_term', 'adid', 'fbclid' ];

export const getLoanAmount = (formData) => {
  return formData[FieldNames.loanAmount]
    ? formData[FieldNames.loanAmount]
    : formData[FieldNames.homeValue] - formData[FieldNames.downDollar];
};

export const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/) ||
    window.location.host.includes(process.env.PORT || '3000'),
);

/**
 * Gets base url from location with an optional route passed in.
 *
 * Example: http://localhost:4000/resume
 *          https://lklecot.prequalexpress.com/resume
 * or
 * Example: https://flagstar2.lenderfulsolutions.com/v2/resume
 *
 * @param {string} [route='']
 * @returns {string}
 */
export const createBaseUrlFromLocation = (route: string = ''): string => {
  const product = getProduct();
  if (isLocalhost || product === PRODUCT.AUTO_PREQUAL ) {
    return `${window.location.origin}/${route}`;
  } else {
    return `${window.location.origin}/v2/${route}`;
  }
};

/**
 * Endpoints found in backend-services
 */
export enum Endpoints {
  AUTO_LOAN_RATES                = 'rates/consumer-loan/auto',
  AUTO_LOAN_LIMITS               = 'rates/consumer-loan/limits/auto',
  AUTO_LOAN_LIMITS_DTI           = 'rates/consumer-loan/debt-to-income/auto',
  AUTO_VALUATION_SEARCH          = 'valuation/auto/search',
  AUTO_VALUATION_DETAILS         = 'valuation/auto/details',
  AUTO_VALUATION_REPORT          = 'valuation/auto/report',
  AUDIT_EVENT                    = 'audit-event',
  CHECKLIST                      = 'file-generation/checklist',
  CHANGE_PASSWORD                = 'auth/password/change-password',
  CLOSING_COSTS_ESTIMATE         = 'file-generation/closing-costs-estimate',
  CONFIG                         = 'client-management/config',
  CONTACT_US_EMAIL               = 'messaging/email',
  CONTACT_US_DATA                = 'messaging/contact',
  CREATE_INSURANCE_QUOTE         = 'insurance-quote',
  CREDIT_REPORT                  = 'vendor/credit-reporting-services',
  CREDIT_BOOSTER_RATES           = 'rates/consumer-loan/credit-booster',
  CREDIT_BOOSTER_LOAN_LIMITS     = 'rates/consumer-loan/limits/credit-booster',
  EMAIL                          = 'file-generation/rates',
  EQUITYRATES                    = 'home-equity/rates',
  HOME_EQUITY_ACCEPTED_LOCATION  = 'home-equity/accepted-location',
  HOME_EQUITY_LOAN_AMOUNT_LIMITS = 'home-equity/loan-amount-limits',
  EMAIL_CHECK                    = 'auth/email/email-check',
  EXPRESS_FORM                   = 'mortgage-applications/express',
  FINANCIAL_STATEMENT                 = 'financial-statement',
  FILE_GENERATION_FINANCIAL_STATEMENT = 'file-generation/financial-statement',
  FINANCIAL_VERIFICATION         = 'verification/customer',
  FINANCIAL_VERIFICATION_TOKEN   = 'verification/token',
  FINANCIAL_ORDER                = 'verification/order',
  GENERATE_PREQUAL_LETTER          = 'file-generation/pdf',
  GET_PREQUAL_LETTER               = 'user-management/prequal-letter',
  LOANS                            = 'loans',
  LOANS_PATCH                      = 'loans/patch',
  LOAN_OFFICER                     = 'client-management/loan-officer',
  LONGFORM                         = 'mortgage-applications/longform',
  OVERDRAFT_PROTECTION_RATES       = 'rates/consumer-loan/overdraft-protection',
  OVERDRAFT_PROTECTION_LOAN_LIMITS = 'rates/consumer-loan/limits/overdraft-protection',
  OVERDRAFT_PROTECTION_DTI         = 'rates/consumer-loan/debt-to-income/overdraft-protection',
  PARTIAL_APPS                     = 'mortgage-applications/partial',
  PERSONAL_LOAN_RATES              = 'rates/consumer-loan/personal',
  PERSONAL_LOAN_LIMITS             = 'rates/consumer-loan/limits/personal',
  PERSONAL_LOAN_DTI                = 'rates/consumer-loan/debt-to-income/personal',
  PREQUALRATES                     = 'rates/pre-qual',
  PROPERTY_VALUATION               = 'valuation/property',
  RATES                            = 'rates',
  REVERSE_MORTGAGE                 = 'rates/reverse-mortgage',
  REVERSE_SUMMARY                  = 'file-generation/reverse-summary',
  SHORTFORM                        = 'mortgage-applications/shortform',
  TRANSLATIONS                     = 'translations/locale',
  VALIDATE_ADDRESS                 = 'vendor/usps',
  VALIDATE_ZIP_CODE                = 'validation/zip',
}

/**
 * Checks the browser URL for query parameter loanPurpose
 * If loanPurpose is found its value is saved to sessionStorage
 *
 * @deprecated don't believe this is used
 * @param {ParsedQueryParams} [parsed={}]
 */
const checkUrlForLoanPurpose = (parsed: ParsedQueryParams = {}): void => {
  if (parsed.loanPurpose) {
    logger.debug('found loanPurpose in query params, setting to sessionStorage', { parsed });
    setLoanPurposeToSessionStorage(parsed.loanPurpose);
  }
};

/**
 * Checks the browser URL for defined tracking parameters
 *
 * @param {unknown} parsed query parameters
 */
const checkUrlForTrackingParams = (parsed: unknown = {}): void => {
  let trackingParams = '';
  TRACKING_KEYS.forEach((param) => {
    if (parsed[param]) {
      logger.debug(`Found TRACKING_KEYS parameter '${param}' in query params, setting to sessionStorage`, { parsed });
      trackingParams += `${param}=${parsed[param]}&`;
    }
  });
  if (trackingParams.length > 0) {
    trackingParams = '?'.concat(trackingParams.slice(0, -1));
    storage.session.setItem('trackingParams', trackingParams);
  }
};

/**
 * Get tracking parameters from session storage if they exist, otherwise return nothing.
 *
 * @returns {string | undefined} tracking parameters string or undefined
 */
export const getTrackingParams = (): string | undefined => {
  const trackingParams = storage.session.getItem('trackingParams');
  if (trackingParams === null) {
    logger.info('No trackingParams from sessionStorage');
    return undefined;
  }
  return trackingParams;
};

export const setQueryParamsToLocalStorage = (): void => {
  const { search } = window.location;
  if (search) {
    const parsed = parse(search);
    checkUrlForLocalizationParam(parsed);
    checkUrlForLoanPurpose(parsed);
    checkUrlForTrackingParams(parsed);
  }
};

/**
 * Checks if the url has a localization param
 *
 * @example
 * /purchase?lng=en
 *
 * @param {ParsedQueryParams} [parsed={}]
 */
const checkUrlForLocalizationParam = (parsed: ParsedQueryParams = {}): void => {
  if (parsed.lng) {
    setLocaleToLocalStorage(parsed.lng);
  }
};

/**
 * Sets a locale to the localStorage key used by i18next
 *
 * @param {string} lng
 */
const setLocaleToLocalStorage = (lng: string): void => {
  logger.debug('Setting i18nextLng in localStorage to: ', lng);
  storage.local.setItem('i18nextLng', lng);
};

/**
 * Get locale key from localStorage
 *
 * @return {string} browser locale as string (i.e. en-US or es-US)
 */
export const getLocale = (): string => {
  return i18next.languages[0] || 'en';
};

/**
 * Sets a value to the loanPurpose key in sessionStorage
 *
 * @deprecated don't believe this is used
 * @param {string} loanPurpose
 */
const setLoanPurposeToSessionStorage = (loanPurpose: string): void => {
  logger.debug('Setting loanPurpose in sessionStorage to: ', loanPurpose);
  storage.session.setItem('loanPurpose', loanPurpose);
};
